import { createContext, useContext, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '@/lib/api'
import { exitImpersonationApi } from '@/lib/api/admin'

const ADMIN_TOKEN_KEY = 'qopr_admin_session_token'
const IMPERSONATED_TOKEN_KEY = 'qopr_impersonated_session_token'
const IMPERSONATED_USER_KEY = 'qopr_impersonated_user'

// Synchronously restore Bearer override before any renders
// so React Query doesn't fire requests without it after a page refresh
const _restoredToken = localStorage.getItem(IMPERSONATED_TOKEN_KEY)
if (_restoredToken) {
  apiClient.defaults.headers.common['Authorization'] = `Bearer ${_restoredToken}`
}

interface ImpersonatedUser {
  id: string
  name: string
  email: string
}

interface ImpersonationContextValue {
  isImpersonating: boolean
  impersonatedUser: ImpersonatedUser | null
  adminSessionToken: string | null
  startImpersonation: (adminToken: string, impersonatedToken: string, targetUser: ImpersonatedUser) => void
  exitImpersonation: () => Promise<void>
}

const ImpersonationContext = createContext<ImpersonationContextValue | null>(null)

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const [adminSessionToken, setAdminSessionToken] = useState<string | null>(() =>
    localStorage.getItem(ADMIN_TOKEN_KEY)
  )
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(() => {
    const raw = localStorage.getItem(IMPERSONATED_USER_KEY)
    return raw ? JSON.parse(raw) : null
  })

  const startImpersonation = useCallback((adminToken: string, impersonatedToken: string, targetUser: ImpersonatedUser) => {
    localStorage.setItem(ADMIN_TOKEN_KEY, adminToken)
    localStorage.setItem(IMPERSONATED_TOKEN_KEY, impersonatedToken)
    localStorage.setItem(IMPERSONATED_USER_KEY, JSON.stringify(targetUser))
    setAdminSessionToken(adminToken)
    setImpersonatedUser(targetUser)
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${impersonatedToken}`
    navigate('/orgs')
  }, [navigate])

  const exitImpersonation = useCallback(async () => {
    const storedAdminToken = localStorage.getItem(ADMIN_TOKEN_KEY)
    if (!storedAdminToken) return

    try {
      await exitImpersonationApi(storedAdminToken)
    } catch {
      // Continue cleanup regardless
    }

    localStorage.removeItem(ADMIN_TOKEN_KEY)
    localStorage.removeItem(IMPERSONATED_TOKEN_KEY)
    localStorage.removeItem(IMPERSONATED_USER_KEY)
    setAdminSessionToken(null)
    setImpersonatedUser(null)
    delete apiClient.defaults.headers.common['Authorization']

    // Hard reload to re-establish cookie-based admin session cleanly
    window.location.href = '/admin/users'
  }, [])

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating: !!adminSessionToken,
        impersonatedUser,
        adminSessionToken,
        startImpersonation,
        exitImpersonation,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  )
}

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext)
  if (!ctx) throw new Error('useImpersonation must be used within an ImpersonationProvider')
  return ctx
}
